import { useMemo, useState } from 'react';

import { copy } from '../../core/copy';
import {
  OPTIONS,
  randomIzgled,
  type Izgled,
  type IzgledAxis,
} from '../../core/look';
import { BEARD, EXTRA, HAIR, OUTFIT, SWATCHES, type Feature } from '../art/features';
import { heroColors, heroLookKey, heroPose } from '../art/sprites';
import { Btn, HeroStage, Sprite } from '../components';
import { sfx } from '../audio';

/**
 * The wardrobe.
 *
 * ## What this screen is not
 *
 * It is not a reward, a shop, or a collection. Every option on it is available to everybody from
 * the first minute, it costs nothing, and nothing on it is counted. That is not squeamishness —
 * GDD §2.2 cut nine cosmetic slots and §10.1 says outright that points must never buy cosmetics,
 * and both of those cuts were about cosmetics as *things to collect*. A wardrobe is a different
 * object: it adds no number, nothing accumulates, and there is nothing here to explain. The
 * souvenirs remain the only earned thing on the character, and this screen deliberately never
 * mentions them — the moment a wardrobe tells you what you have not got, it has become a shop.
 *
 * ## Why it is shaped like this
 *
 * **The preview walks.** Half the art in this game only exists in motion — the bob, the swinging
 * arm, hair with volume in it — and a wardrobe showing a static doll is a form with a picture on
 * it. It also turns, because four of the ten hairstyles are mostly a decision about the back of
 * somebody's head and you cannot choose a ponytail you cannot see.
 *
 * **Every choice is a picture of itself.** No names in the grid, no numbers, no swatch that stands
 * for something else: a hairstyle is a drawing of that hairstyle on *your* character, with your
 * colours already on it, and a colour is that colour. This is the same rule the rest of the product
 * follows about not making anybody decode an icon (GDD §2.1) — applied to a screen with nine rows
 * on it, where it matters most.
 *
 * **Three tabs, never nine rows.** The one risk this project actually carries is that an app built
 * to cure overwhelm becomes overwhelming. Nine rows of options on one screen is exactly that, and
 * the tabs cost nothing: `Lice` is who you are, `Kosa` is the loudest single decision, `Odeća` is
 * what you are wearing.
 */

/* ------------------------------------------------------------------ the tabs */

interface Tab {
  id: 'lice' | 'kosa' | 'odeca';
  label: string;
  axes: IzgledAxis[];
}

const TABS: readonly Tab[] = [
  { id: 'lice', label: copy.izgled.tabs.lice, axes: ['skin', 'eyes', 'beard', 'extra'] },
  { id: 'kosa', label: copy.izgled.tabs.kosa, axes: ['hair', 'hairColor'] },
  { id: 'odeca', label: copy.izgled.tabs.odeca, axes: ['outfit', 'outfitColor', 'trousers'] },
];

/** Which axes are drawings and which are colours. Everything below branches on exactly this. */
const SHAPES: Partial<Record<IzgledAxis, readonly Feature[]>> = {
  hair: HAIR,
  beard: BEARD,
  outfit: OUTFIT,
  extra: EXTRA,
};

/**
 * Where a thumbnail is cropped to.
 *
 * A hairstyle thumbnail showing a whole body is a row of twelve identical people with slightly
 * different heads, and at thumbnail size the head is the part you cannot see. So each axis is
 * cropped to the rows it actually changes — which is also what makes the row scannable in one
 * pass rather than one item at a time.
 */
const CROP: Record<string, [number, number]> = {
  hair: [1, 11],
  beard: [3, 11],
  extra: [2, 9],
  outfit: [8, 17],
};

/**
 * Three, not two.
 *
 * A beard is two rows of pixels. At scale 2 that is four screen pixels of difference between six
 * options, which is a row of six identical buttons — the first version of this screen shipped that
 * and it was unusable. Three is the smallest scale at which every axis is decidable at a glance,
 * and it is why the rows scroll sideways instead of fitting.
 */
const THUMB = 3;

const FACINGS = ['down', 'left', 'up', 'right'] as const;
type Facing = (typeof FACINGS)[number];

/* ------------------------------------------------------------------ one option */

function ShapeThumb({
  axis,
  option,
  izgled,
  selected,
  onPick,
}: {
  axis: IzgledAxis;
  option: number;
  izgled: Izgled;
  selected: boolean;
  onPick: () => void;
}) {
  const feature = SHAPES[axis]![option]!;
  // The candidate drawn on *this* character rather than on a mannequin. Choosing a beard against
  // somebody else's skin tone is choosing blind, and at eight tones it is visibly the wrong beard.
  const candidate = { ...izgled, [axis]: option } as Izgled;
  const [from, to] = CROP[axis] ?? [0, 20];
  const { key, rows } = heroPose('down', 'stand', candidate);
  // Memoised on the appearance *string*, not on the object: `candidate` is rebuilt every render, so
  // a dependency on it memoises nothing and hands `Sprite` a new colour map each time, which
  // re-runs its draw effect on every keystroke in a row of thirty thumbnails.
  const look = heroLookKey(candidate);
  const colors = useMemo(() => heroColors(candidate), [look]);

  return (
    <button
      className={`izbtn ${selected ? 'on' : ''}`.trim()}
      onClick={onPick}
      aria-pressed={selected}
      aria-label={feature.label}
      title={feature.label}
    >
      <Sprite
        rows={rows.slice(from, to)}
        colors={colors}
        scale={THUMB}
        /*
         * The whole appearance is in the key, not just the shape.
         *
         * The bakery caches finished canvases by string and lives for the life of the tab, so a key
         * that names only the hairstyle hands back the version that was baked in whatever colour
         * was current the first time this row rendered. Dyeing your hair then repainted the preview
         * and left every thumbnail brown — the picker stopped agreeing with the character.
         */
        cacheKey={`thumb:${axis}:${option}:${look}:${key}:${from}`}
      />
    </button>
  );
}

function ColorChip({
  color,
  selected,
  onPick,
  label,
}: {
  color: string;
  selected: boolean;
  onPick: () => void;
  label: string;
}) {
  return (
    <button
      className={`izchip ${selected ? 'on' : ''}`.trim()}
      style={{ background: color }}
      onClick={onPick}
      aria-pressed={selected}
      aria-label={label}
      title={label}
    />
  );
}

function AxisRow({
  axis,
  izgled,
  onSet,
}: {
  axis: IzgledAxis;
  izgled: Izgled;
  onSet: (axis: IzgledAxis, value: number) => void;
}) {
  const count = OPTIONS[axis];
  const shapes = SHAPES[axis];
  const swatches = (SWATCHES as Partial<Record<IzgledAxis, readonly string[]>>)[axis];

  return (
    <div className="izaxis">
      <p className="lbl">{copy.izgled.axes[axis]}</p>
      <div className={shapes ? 'izrow shapes' : 'izrow chips'}>
        {Array.from({ length: count }, (_, option) =>
          shapes ? (
            <ShapeThumb
              key={option}
              axis={axis}
              option={option}
              izgled={izgled}
              selected={izgled[axis] === option}
              onPick={() => onSet(axis, option)}
            />
          ) : (
            <ColorChip
              key={option}
              color={swatches?.[option] ?? '#000'}
              selected={izgled[axis] === option}
              onPick={() => onSet(axis, option)}
              label={`${copy.izgled.axes[axis]} ${option + 1}`}
            />
          ),
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ the editor */

export function IzgledEditor({
  value,
  souvenirs = [],
  onChange,
}: {
  value: Izgled;
  /** What they have earned, worn on the preview. Read-only here — this screen does not grant. */
  souvenirs?: readonly string[];
  onChange: (next: Izgled) => void;
}) {
  const [tab, setTab] = useState<Tab['id']>('lice');
  const [facing, setFacing] = useState<Facing>('down');
  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!;

  const set = (axis: IzgledAxis, option: number) => {
    sfx.move();
    onChange({ ...value, [axis]: option });
  };

  return (
    <div className="izgled">
      <div className="izstage">
        <HeroStage izgled={value} facing={facing} souvenirs={souvenirs} scale={6} />
        <button
          className="btn ghost small izturn"
          onClick={() => {
            sfx.move();
            setFacing(FACINGS[(FACINGS.indexOf(facing) + 1) % FACINGS.length]!);
          }}
        >
          {copy.izgled.turn}
        </button>
      </div>

      <div className="iztabs" role="tablist">
        {TABS.map((option) => (
          <button
            key={option.id}
            role="tab"
            aria-selected={tab === option.id}
            className={`iztab ${tab === option.id ? 'on' : ''}`.trim()}
            onClick={() => {
              sfx.move();
              setTab(option.id);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="izbody">
        {active.axes.map((axis) => (
          <AxisRow key={axis} axis={axis} izgled={value} onSet={set} />
        ))}
      </div>

      <div className="btn-row izfoot">
        <Btn
          variant="ghost"
          onClick={() => {
            sfx.move();
            onChange(randomIzgled());
          }}
        >
          {copy.izgled.random}
        </Btn>
      </div>

      <p className="tiny muted">{copy.izgled.hint}</p>
    </div>
  );
}
